import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const CART_KEY = "tn-cart-id";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const persist = (c) => {
    setCart(c);
    if (c?.id) localStorage.setItem(CART_KEY, c.id);
  };

  useEffect(() => {
    const id = localStorage.getItem(CART_KEY);
    if (!id) return;
    api
      .get(`/cart/${encodeURIComponent(id)}`)
      .then((r) => setCart(r.data.cart))
      .catch(() => localStorage.removeItem(CART_KEY));
  }, []);

  const errMsg = (e) => {
    const detail = e.response?.data?.detail || "";
    if (detail.toLowerCase().includes("not configured"))
      return "Checkout unlocks when Shopify connects — this is the preview catalog";
    return detail || "Cart unavailable right now — try again";
  };

  const addItem = async (merchandiseId, quantity = 1, { silent = false } = {}) => {
    setBusy(true);
    try {
      const lines = [{ merchandiseId, quantity }];
      const r = cart?.id
        ? await api.post(`/cart/${encodeURIComponent(cart.id)}/lines`, { lines })
        : await api.post("/cart", { lines });
      persist(r.data.cart);
      if (!silent) {
        setOpen(true);
        toast.success("Added to cart");
      }
      return r.data.cart;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const updateItem = async (lineId, quantity) => {
    if (!cart?.id) return;
    setBusy(true);
    try {
      const r = await api.put(`/cart/${encodeURIComponent(cart.id)}/lines`, {
        lines: [{ id: lineId, quantity }],
      });
      persist(r.data.cart);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (lineId) => {
    if (!cart?.id) return;
    setBusy(true);
    try {
      const r = await api.delete(`/cart/${encodeURIComponent(cart.id)}/lines`, {
        data: { lineIds: [lineId] },
      });
      persist(r.data.cart);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const checkout = () => {
    if (cart?.checkoutUrl) window.location.href = cart.checkoutUrl;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        open,
        setOpen,
        busy,
        addItem,
        updateItem,
        removeItem,
        checkout,
        count: cart?.totalQuantity || 0,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
